import { useState } from "react";
import { Button, Modal, Pagination, Table, Tag, Tooltip, Typography } from "antd";
import Search from "antd/lib/input/Search";
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import type { ColumnsType } from "antd/es/table";
import Layout from "../../Containers/Layout";
import styles from "./MyDisciplines.module.scss";
import { MyDisciplineDTO, StudyCycleEnum, SubjectStatusEnum } from "../../Api";
import { getMyDisciplines, downloadPdfFile, updateSubjectStatus } from "../../Requests/subject-requests";
import { downloadFile } from "../../utils/downloadUtils";
import { getUserId } from "../../utils/utilFunctions";
import { getSchoolYear, parseSubjectStatusValidationErrors } from "../Subject/helpers";
import { NOTIFICATION_TYPES, openNotification } from "../Notifications/NotificationsUtils";
import SubjectStatusHistoryModal from "../Subject/SubjectStatusHistoryModal";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

const MyDisciplines = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [modalPropsHistory, setModalPropsHistory] = useState({
        isModalVisible: false,
        subjectId: '',
        subjectName: '',
    });

    const [modalPropsValidationErrors, setModalPropsValidationErrors] = useState<{ isModalVisible: boolean, errors: string[] }>({
        isModalVisible: false,
        errors: [],
    });

    const handleCloseValidationErrors = () => {
        setModalPropsValidationErrors(modalProps => ({ ...modalProps, isModalVisible: false }));
    };

    const schoolYear = getSchoolYear(new Date());

    const success = (message: string) =>
        openNotification("Succes", message, NOTIFICATION_TYPES.SUCCESS);

    const failed = (message: string) =>
        openNotification("Eroare", message, NOTIFICATION_TYPES.ERROR);

    const { data, isLoading } = useQuery(
        ["getMyDisciplines", searchTerm, page, pageSize, schoolYear],
        () => getMyDisciplines(searchTerm, schoolYear, page, pageSize),
        { onError: () => failed("Datele nu au putut fi preluate de la server!") }
    );

    const statusColors = (status?: SubjectStatusEnum) => {
        switch (status) {
            case SubjectStatusEnum.Draft: return "red";
            case SubjectStatusEnum.PendingDirectorValidation: return "magenta";
            case SubjectStatusEnum.PendingDeanValidation: return "orange";
            case SubjectStatusEnum.PendingQaValidation: return "gold";
            case SubjectStatusEnum.Approved: return "green";
            default: return "default";
        }
    };

    const statusToString = (status?: SubjectStatusEnum) => {
        switch (status) {
            case SubjectStatusEnum.Draft: return "Draft";
            case SubjectStatusEnum.PendingDirectorValidation: return "Validare Departament";
            case SubjectStatusEnum.PendingDeanValidation: return "Validare Decan";
            case SubjectStatusEnum.PendingQaValidation: return "Validare Calitate";
            case SubjectStatusEnum.Approved: return "Aprobat";
            default: return "N/A";
        }
    };

    const handlePdfDownload = (record: MyDisciplineDTO) => {
        downloadFile(
            downloadPdfFile(record.id!), "Fișă Disciplină " + record.nameRO
        ).catch(() => failed("Descărcarea nu a fost efectuată!"));
    };

    const handlePdfPreview = async (record: MyDisciplineDTO) => {
        const win = window.open("about:blank", "_blank");
        if (!win) {
            failed("Preview-ul nu a putut fi deschis!");
            return;
        }
        try {
            const res = await downloadPdfFile(record.id!);
            const blob = res instanceof Blob ? res : (res as any).data;
            const pdfBlob = blob instanceof Blob ? blob : new Blob([blob], { type: "application/pdf" });
            const url = URL.createObjectURL(pdfBlob);
            win.location.href = url;
            win.document.title = `Fișă Disciplină ${record.nameRO ?? ""}`.trim();
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch {
            win.close();
            failed("Preview-ul nu a putut fi deschis!");
        }
    };

    const isTitular = (record: MyDisciplineDTO) =>
        record.courseCoordinators?.some(c => c.id === getUserId())
        || (record.courseWeeklyHours === 0 && record.labCoordinators?.some(c => c.id === getUserId()));

    const canEdit = (record: MyDisciplineDTO) =>
        record.status === SubjectStatusEnum.Draft;

    const submitInfo = (record: MyDisciplineDTO) => {
        if (record.status !== SubjectStatusEnum.Draft || !isTitular(record)) {
            return null;
        }
        const isFacultyManaged = !record.departmentId || record.departmentId === EMPTY_GUID;
        return {
            nextStatus: isFacultyManaged
                ? SubjectStatusEnum.PendingDeanValidation
                : SubjectStatusEnum.PendingDirectorValidation,
            disabled: !!record.isPartiallySaved,
        };
    };

    const handleSubmitForValidation = async (record: MyDisciplineDTO) => {
        const info = submitInfo(record);
        if (!info || info.disabled) return;
        try {
            await updateSubjectStatus(record.id!, info.nextStatus, "Trimitere spre validare");
            success("Statusul a fost actualizat cu succes!");
            queryClient.invalidateQueries("getMyDisciplines");
        } catch (err: any) {
            const validationErrors = await parseSubjectStatusValidationErrors(err);
            if (validationErrors) {
                setModalPropsValidationErrors({ isModalVisible: true, errors: validationErrors });
                return;
            }
            const errMsg = err?.text ? await err.text() : "";
            failed("Statusul nu a putut fi actualizat! " + errMsg);
        }
    };

    const handleShowHistory = (record: MyDisciplineDTO) => {
        setModalPropsHistory({ isModalVisible: true, subjectId: record.id!, subjectName: record.nameRO ?? '' });
    };

    const handleCloseHistory = () => {
        setModalPropsHistory(modalPropsHistory => ({ ...modalPropsHistory, isModalVisible: false }));
    };

    const FilterByNameInput = (
        <Search
            placeholder="Nume disciplină"
            allowClear
            onSearch={(value) => { setPage(1); setSearchTerm(value); }}
            style={{ width: "100%" }}
        />
    );

    const columns: ColumnsType<MyDisciplineDTO> = [
        { title: "Cod disciplină", dataIndex: "code", key: "code", width: 170 },
        { title: FilterByNameInput, dataIndex: "nameRO", key: "nameRO", width: 380 },
        { title: "An studiu", dataIndex: "studyYear", key: "studyYear", width: 100 },
        { title: "Semestru", dataIndex: "semester", key: "semester", width: 100 },
        { title: "Program de studiu", dataIndex: "programName", key: "programName", width: 180 },
        { title: "Facultate", dataIndex: "facultyName", key: "facultyName", width: 180 },
        {
            title: "Status fișă",
            dataIndex: "status",
            key: "status",
            width: 170,
            render: (_: any, record) => (
                <Tag color={statusColors(record.status)} key={record.status}>
                    {statusToString(record.status)}
                </Tag>
            ),
        },
        {
            title: "Acțiuni",
            key: "actions",
            width: 160,
            fixed: "right",
            render: (_: any, record) => {
                const info = submitInfo(record);
                return (
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <Tooltip title="Descărcare">
                            <FontAwesomeIcon
                                icon={solid("file-pdf")}
                                style={{ cursor: "pointer", height: "1rem", padding: "6px 0px" }}
                                onClick={() => handlePdfDownload(record)}
                            />
                        </Tooltip>
                        <Tooltip title="Vizualizare">
                            <FontAwesomeIcon
                                icon={solid("eye")}
                                style={{ marginLeft: "20px", cursor: "pointer", height: "1rem", padding: "6px 0px" }}
                                onClick={() => handlePdfPreview(record)}
                            />
                        </Tooltip>
                        {canEdit(record) && (
                            <Link to={{
                                pathname: "/" + (record.studyCycle ?? StudyCycleEnum.Bachelor) + "/editare-disciplina/" + record.studyProgramId + "/" + record.id,
                                state: { from: "/disciplinele-mele" }
                            }}>
                                <Tooltip title="Editare fișă disciplină">
                                    <FontAwesomeIcon
                                        icon={solid("edit")}
                                        style={{ marginLeft: "20px", cursor: "pointer", height: "1rem", padding: "6px 0px" }}
                                    />
                                </Tooltip>
                            </Link>
                        )}
                        {info != null && (
                            <Tooltip title={info.disabled
                                ? "Fișa este salvată parțial. Faceți o salvare normală înainte de trimitere spre validare."
                                : "Trimitere spre validare"}>
                                <FontAwesomeIcon
                                    icon={solid("paper-plane")}
                                    style={{
                                        marginLeft: "20px",
                                        cursor: info.disabled ? "not-allowed" : "pointer",
                                        height: "1rem",
                                        padding: "6px 0px",
                                        opacity: info.disabled ? 0.5 : 1,
                                    }}
                                    onClick={() => handleSubmitForValidation(record)}
                                />
                            </Tooltip>
                        )}
                        <Tooltip title="Istoric status">
                            <FontAwesomeIcon
                                icon={solid("clock-rotate-left")}
                                style={{ marginLeft: "20px", cursor: "pointer", height: "1rem", padding: "6px 0px" }}
                                onClick={() => handleShowHistory(record)}
                            />
                        </Tooltip>
                    </div>
                );
            },
        },
    ];

    return (
        <div className={styles.container} style={{ display: "flex", flexDirection: "column", marginTop: "0%" }}>
            <Layout>
                <div style={{ display: "flex", justifyContent: "flex-end", margin: "16px 0" }}>
                    <Pagination
                        total={data?.totalCount}
                        current={page}
                        pageSize={pageSize}
                        pageSizeOptions={["10", "20", "50"]}
                        defaultPageSize={10}
                        showSizeChanger={true}
                        onChange={(newPage, newPageSize) => {
                            setPage(newPage);
                            setPageSize(newPageSize);
                        }}
                        showTotal={() => (
                            <Button type="default">
                                Total discipline: {data?.totalCount ?? 0}
                            </Button>
                        )}
                        hideOnSinglePage={false}
                        locale={{ items_per_page: t("configuration.pagination") }}
                    />
                </div>
                <div>
                    <Table
                        className={styles.disciplinesTable}
                        dataSource={data?.data || []}
                        scroll={{ x: 1200, y: "calc(100vh - 380px)" }}
                        columns={columns}
                        rowKey={(record) => record.id!}
                        loading={isLoading}
                        pagination={false}
                        sticky={true}
                        locale={{ emptyText: <h3>Nu există discipline asignate pentru planul curent.</h3> }}
                    />
                </div>
                <SubjectStatusHistoryModal
                    open={modalPropsHistory.isModalVisible}
                    subjectId={modalPropsHistory.subjectId}
                    subjectName={modalPropsHistory.subjectName}
                    onCancel={handleCloseHistory}
                    statusColors={statusColors}
                    statusToString={statusToString}
                />
                <Modal
                    open={modalPropsValidationErrors.isModalVisible}
                    onOk={handleCloseValidationErrors}
                    onCancel={handleCloseValidationErrors}
                    title="Validarea a eșuat"
                    width={600}
                    cancelButtonProps={{ style: { display: "none" } }}
                >
                    <div>
                        {modalPropsValidationErrors.errors.map(message => <Typography key={message}>- {message}</Typography>)}
                    </div>
                </Modal>
            </Layout>
        </div>
    );
};

export default MyDisciplines;
